// src/components/layouts/layout-components/NavLanguage.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useCallback } from 'react';
import useZustand from 'hooks/useZustand';
import { CheckOutlined, DownOutlined, GlobalOutlined } from '@ant-design/icons';
import { Dropdown, Grid, Tooltip } from 'antd';
import translateX from 'utils/translateX';
import { css } from '@emotion/react';
import StyledNavItem from './StyledNavItem';
import Flex from 'components/ui/Flex';
import countriesProfiles from 'assets/data/countriesProfiles.json';
import utils from 'utils/utils';
import { SPACER } from 'constants/ThemeConstant';
import { getFlag } from 'utils/phone';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;

const flagImgStyle = { width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' };

const LANG_OPTIONS = [
  { id: 'pt-br', short: 'pt', name: 'Portuguese (BR)', icon: 'BR' },
  { id: 'pt-pt', short: 'pt-pt', name: 'Portuguese (PT)', icon: 'PT' },
  { id: 'en-us', short: 'en', name: 'English', icon: 'US' },
  { id: 'es-es', short: 'es', name: 'Spanish', icon: 'ES' },
];

// *******************************************************************************
// COMPONENTS: SelectedLanguage
// *******************************************************************************
const SelectedLanguage = () => {
  // FIX: Use granular selector to prevent unnecessary re-renders
  const storedLanguage = useZustand('theme', 'language');

  const details = useMemo(() => matchCountryProfile(storedLanguage), [storedLanguage]);

  return (
    <Flex alignItems="center">
      <img
        style={flagImgStyle}
        src={getFlag(details.icon) || undefined}
        alt={details.langName}
        onError={e => { e.target.style.display = 'none'; }}
      />
      <span className="font-weight-semibold ml-2">
        {details.langName} <DownOutlined className="font-size-xs" />
      </span>
    </Flex>
  );
};

// *******************************************************************************
// COMPONENTS: MenuItem
// *******************************************************************************
const MenuItem = ({ icon, langName, langId }) => {
  // FIX: Use granular selectors
  const storedLanguage = useZustand('theme', 'language');
  const setLanguageStore = useZustand('theme', 'setLanguage');

  const handleLocaleChange = useCallback(
    async target => {
      const canon = toCanonical(target);

      try {
        // Call Zustand setLanguageStore which handles everything:
        // - i18next.changeLanguage()
        // - dayjs.locale()
        // - localStorage updates
        // - axios headers
        // - Zustand state update
        await setLanguageStore(canon);
      } catch (e) {
        console.error('[NavLanguage] changeLanguage error:', e);
      }
    },
    [setLanguageStore]
  );

  const isActive = toCanonical(storedLanguage) === toCanonical(langId);

  return (
    <div
      onClick={() => handleLocaleChange(langId)}
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        min-height: 40px;
        cursor: pointer;
        padding: 8px 12px;
        transition: background-color 0.2s;
        border-radius: 0 !important;

        /* inner children should not capture pointer events */
        > * {
          pointer-events: none;
        }
      `}
    >
      <Flex alignItems="center" gap={SPACER[2]}>
        <img
          style={flagImgStyle}
          src={getFlag(icon) || undefined}
          alt={langName}
          onError={e => { e.target.style.display = 'none'; }}
        />
        <span style={{ whiteSpace: 'nowrap' }}>{langName}</span>
      </Flex>
      {isActive && <CheckOutlined className="text-success" />}
    </div>
  );
};

// *******************************************************************************
// COMPONENT: NavLanguage (MAIN)
// *******************************************************************************
export const Language = ({ configDisplay, mode, onFixed }) => {
  // 7.1 HOOKS & REFS
  const bp = useBreakpoint();

  // 7.2 LOCAL VARIABLES
  const isMobile = !utils.getBreakPoint(bp).includes('lg');
  const placement = isMobile ? (onFixed === 'footer' ? 'top' : 'bottomRight') : 'topRight';

  // 7.3 MENU DATA (AntD v5)
  const menuItems = [
    {
      key: 'pt-br',
      label: <MenuItem icon="BR" langName="Portuguese (BR)" langId="pt-br" />,
    },
    {
      key: 'pt-pt',
      label: <MenuItem icon="PT" langName="Portuguese (PT)" langId="pt-pt" />,
    },
    {
      key: 'en-us',
      label: <MenuItem icon="US" langName="English" langId="en-us" />,
    },
    {
      key: 'es-es',
      label: <MenuItem icon="ES" langName="Spanish" langId="es-es" />,
    },
  ];

  // 7.4 RENDER
  return (
    <Dropdown
      placement={placement}
      menu={{ items: menuItems }}
      trigger={['click']}
      styles={{
        root: {
          minWidth: '200px', // wide enough for "Portuguese (PT)" + checkmark without wrapping
        },
      }}
      popupRender={menu => (
        <div
          css={css`
            .ant-dropdown-menu {
              padding: 0 !important;
            }
            .ant-dropdown-menu-item {
              padding: 0 !important;
              border-radius: 0 !important;
              height: auto !important;
            }
          `}
        >
          {menu}
        </div>
      )}
    >
      {configDisplay ? (
        <div>
          <SelectedLanguage />
        </div>
      ) : (
        <Tooltip title={translateX('change_language')} placement="left">
          <StyledNavItem mode={mode}>
            <GlobalOutlined className="nav-icon mr-0 font-size-md" />
          </StyledNavItem>
        </Tooltip>
      )}
    </Dropdown>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default Language;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeLocale(val) {
  return val.toString().trim().toLowerCase().replace('_', '-');
}

function toCanonical(val) {
  const v = normalizeLocale(val);
  if (v === 'pt-pt') return 'pt-pt';
  if (v === 'pt' || v === 'pt-br' || v.startsWith('pt')) return 'pt-br';
  if (v === 'en' || v === 'en-us' || v.startsWith('en')) return 'en-us';
  if (v === 'es-es' || v === 'es' || v.startsWith('es')) return 'es-es';
  return 'en-us';
}

function matchCountryProfile(locale) {
  const canon = toCanonical(locale);
  const found =
    countriesProfiles?.filter(c => c.enabled)?.find(e => normalizeLocale(e.langId) === canon) ||
    countriesProfiles?.filter(c => c.enabled)?.find(e => toCanonical(e.langId) === canon);
  if (found) return found;

  const opt = LANG_OPTIONS.find(o => o.id === canon);
  return {
    langId: canon,
    langName: opt?.name ?? canon,
    icon: opt?.icon ?? 'US',
  };
}
